import { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import type { EmailTemplate, EmailTemplateVersion } from '@/mocks/superAdminEmailTemplates';

interface EmailPreviewModalProps {
  template: EmailTemplate | null;
  onClose: () => void;
  onSave: (id: number, patch: Partial<EmailTemplate>) => void;
  onDuplicate: (t: EmailTemplate) => void;
}

function showAlert(icon: 'success' | 'info' | 'warning', title: string, text: string) {
  return Swal.fire({
    icon,
    title,
    text,
    timer: 2500,
    showConfirmButton: true,
    confirmButtonColor: '#1a1a1a',
    customClass: {
      popup: 'rounded-2xl text-sm',
      title: 'text-sm font-bold',
      confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
    },
  });
}

export default function EmailPreviewModal({ template, onClose, onSave, onDuplicate }: EmailPreviewModalProps) {
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSubtitle, setEditSubtitle] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editCtaText, setEditCtaText] = useState('');
  const [editGreeting, setEditGreeting] = useState('');
  const [showVersions, setShowVersions] = useState(false);

  const currentTemplate = template;

  const activeVersionData = useMemo(() => {
    if (!currentTemplate) return null;
    const av = currentTemplate.versions.find((v) => v.version === currentTemplate.activeVersion);
    return av || currentTemplate.versions[currentTemplate.versions.length - 1];
  }, [currentTemplate, currentTemplate?.versions, currentTemplate?.activeVersion]);

  if (!currentTemplate) return null;

  const enterEdit = () => {
    setEditTitle(currentTemplate.title);
    setEditSubtitle(currentTemplate.subtitle);
    setEditSubject(currentTemplate.subject);
    setEditMessage(currentTemplate.message);
    setEditCtaText(currentTemplate.ctaText);
    setEditGreeting(currentTemplate.greeting);
    setEditMode(true);
    setShowVersions(false);
  };

  const handleSave = () => {
    const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
    const newVersion = (currentTemplate.versions.length > 0
      ? Math.max(...currentTemplate.versions.map((v) => v.version))
      : 0) + 1;

    const newVersionEntry: EmailTemplateVersion = {
      version: newVersion,
      title: editTitle,
      subject: editSubject,
      subtitle: editSubtitle,
      greeting: editGreeting,
      message: editMessage,
      ctaText: editCtaText,
      createdAt: now,
      createdBy: 'Vos (SA)',
    };

    onSave(currentTemplate.id, {
      title: editTitle,
      subtitle: editSubtitle,
      subject: editSubject,
      message: editMessage,
      ctaText: editCtaText,
      greeting: editGreeting,
      lastEdited: now,
      lastEditedBy: 'Vos (SA)',
      activeVersion: newVersion,
      versions: [...currentTemplate.versions, newVersionEntry],
    } as Partial<EmailTemplate>);
    setEditMode(false);
    showAlert('success', 'Template guardado', 'Los cambios fueron guardados y se creó la versión ' + newVersion + ' (simulación).');
  };

  const handleTestSend = () => {
    Swal.fire({
      icon: 'info',
      title: 'Envío de prueba',
      html: `
        <div class="text-xs text-left space-y-2">
          <p>El template <strong>"${currentTemplate.label}"</strong> fue enviado a:</p>
          <div class="bg-background-100 rounded-lg p-3 font-mono text-[10px]">test@radiofrecuenciaweb.com</div>
          <p class="text-foreground-400">Simulación — No se envió ningún correo real.</p>
        </div>
      `,
      timer: 3000,
      showConfirmButton: true,
      confirmButtonColor: '#1a1a1a',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  };

  const handleActivateVersion = (version: EmailTemplateVersion) => {
    Swal.fire({
      icon: 'question',
      title: '¿Activar esta versión?',
      html: `
        <div class="text-xs text-left space-y-2">
          <p>Vas a activar la <strong>versión ${version.version}</strong> del template <strong>"${currentTemplate.label}"</strong>.</p>
          <div class="bg-background-100 rounded-lg p-2 text-[10px]">
            <p><strong>Título:</strong> ${version.title}</p>
            <p><strong>Subtítulo:</strong> ${version.subtitle}</p>
            <p><strong>Asunto:</strong> ${version.subject}</p>
            <p><strong>Creada por:</strong> ${version.createdBy}</p>
            <p><strong>Fecha:</strong> ${version.createdAt}</p>
          </div>
          <p class="text-foreground-400">Los emails enviados a partir de ahora usarán esta versión.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#9ca3af',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        onSave(currentTemplate.id, {
          activeVersion: version.version,
          title: version.title,
          subtitle: version.subtitle,
          subject: version.subject,
          greeting: version.greeting,
          message: version.message,
          ctaText: version.ctaText,
        } as Partial<EmailTemplate>);
        showAlert('success', 'Versión activada', `La versión ${version.version} ahora es la activa para "${currentTemplate.label}".`);
      }
    });
  };

  const handleDuplicate = () => {
    Swal.fire({
      icon: 'question',
      title: '¿Duplicar template?',
      text: `Se creará una copia de "${currentTemplate.label}" con todas sus versiones.`,
      showCancelButton: true,
      confirmButtonText: 'Duplicar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#9ca3af',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        onDuplicate(currentTemplate);
        onClose();
      }
    });
  };

  const title = editMode ? editTitle : currentTemplate.title;
  const subtitle = editMode ? editSubtitle : currentTemplate.subtitle;
  const message = editMode ? editMessage : currentTemplate.message;
  const ctaText = editMode ? editCtaText : currentTemplate.ctaText;
  const greeting = editMode ? editGreeting : currentTemplate.greeting;

  const sortedVersions = [...currentTemplate.versions].sort((a, b) => b.version - a.version);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative w-full max-w-[820px] bg-background-50 rounded-3xl shadow-2xl overflow-hidden mx-4">
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${currentTemplate.icon} text-foreground-600 text-sm`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-800 text-xs font-semibold truncate">{currentTemplate.label}</p>
              <p className="text-foreground-400 text-[10px]">
                {editMode ? 'Modo edición' : showVersions ? 'Historial de versiones' : `v${currentTemplate.activeVersion} activa · Vista previa`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setShowVersions(!showVersions); if (editMode) setEditMode(false); }}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap
                ${showVersions
                  ? 'bg-foreground-950 text-background-50 border-foreground-950'
                  : 'border-background-200 text-foreground-600 hover:bg-background-100'
                }`}
            >
              <i className="ri-git-branch-line text-xs"></i>
              Versiones
            </button>
            <button
              onClick={handleDuplicate}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-background-200 text-foreground-600 text-[10px] font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line text-xs"></i>
              Duplicar
            </button>
            <button
              onClick={handleTestSend}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-foreground-300 text-foreground-600 text-[10px] font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-line text-xs"></i>
              Prueba
            </button>
            {editMode ? (
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-xs"></i>
                Guardar
              </button>
            ) : (
              <button
                onClick={enterEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line text-xs"></i>
                Editar
              </button>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Version history panel */}
        {showVersions && (
          <div className="border-b border-background-200 bg-background-100/50 px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-foreground-800 text-xs font-semibold">Historial de versiones</p>
              <span className="text-foreground-400 text-[10px]">{currentTemplate.versions.length} versión{currentTemplate.versions.length !== 1 ? 'es' : ''}</span>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {sortedVersions.map((v) => {
                const isActive = v.version === currentTemplate.activeVersion;
                return (
                  <div
                    key={v.version}
                    className={`flex items-start justify-between gap-3 p-3 rounded-xl border transition-colors
                      ${isActive
                        ? 'bg-foreground-950/5 border-foreground-300'
                        : 'bg-background-50 border-background-200'
                      }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold ${isActive ? 'text-foreground-950' : 'text-foreground-700'}`}>
                          v{v.version}
                        </span>
                        {isActive && (
                          <span className="px-1.5 py-0.5 rounded-full bg-foreground-950 text-background-50 text-[8px] font-bold">
                            ACTIVA
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-800 text-[10px] font-medium truncate">{v.title}</p>
                      <p className="text-foreground-500 text-[9px] truncate">{v.subtitle}</p>
                      <p className="text-foreground-400 text-[9px] truncate">{v.subject}</p>
                      <div className="flex items-center gap-2 mt-1 text-foreground-400 text-[8px]">
                        <span className="flex items-center gap-1"><i className="ri-user-line text-[7px]"></i>{v.createdBy}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line text-[7px]"></i>{v.createdAt}</span>
                      </div>
                    </div>
                    {!isActive && (
                      <button
                        onClick={() => handleActivateVersion(v)}
                        className="shrink-0 px-2.5 py-1.5 rounded-lg bg-background-100 border border-background-200 text-foreground-600 text-[9px] font-medium hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Activar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Email preview */}
        {!showVersions && (
          <div className="p-5">
            <div className="max-w-[640px] mx-auto border border-background-200 rounded-2xl overflow-hidden shadow-sm bg-white">
              {/* ========== HEADER ========== */}
              <div className="bg-background-50 border-b border-background-200 px-6 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-md bg-foreground-950 flex items-center justify-center">
                    <i className="ri-radio-line text-white text-xs"></i>
                  </div>
                  <span className="text-foreground-800 text-xs font-bold tracking-tight">RadioFrecuenciaWeb</span>
                </div>
                <span className="text-foreground-400 text-[9px]">Plataforma de Radios Online</span>
              </div>

              {/* ========== HERO ========== */}
              <div
                className="relative px-8 py-16 text-center"
                style={{ backgroundColor: currentTemplate.heroBgColor }}
              >
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: currentTemplate.heroOverlayColor }}
                ></div>
                <div className="relative z-10 space-y-3">
                  {/* Logo centered in hero */}
                  <div className="flex justify-center mb-2">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                      <i className="ri-radio-line text-white text-2xl"></i>
                    </div>
                  </div>
                  {/* Title */}
                  {editMode ? (
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full text-center px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-base font-bold outline-none placeholder-white/50"
                      placeholder="Título principal"
                    />
                  ) : (
                    <h2 className="text-white text-[15px] font-bold leading-tight tracking-tight">{title}</h2>
                  )}
                  {/* Subtitle */}
                  {editMode ? (
                    <input
                      type="text"
                      value={editSubtitle}
                      onChange={(e) => setEditSubtitle(e.target.value)}
                      className="w-full text-center px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white/90 text-xs outline-none placeholder-white/50"
                      placeholder="Subtítulo"
                    />
                  ) : (
                    <p className="text-white/80 text-[11px] font-medium leading-relaxed max-w-[420px] mx-auto">{subtitle}</p>
                  )}
                </div>
              </div>

              {/* ========== BODY ========== */}
              <div className="px-8 py-7 bg-white space-y-4">
                {/* Greeting */}
                {editMode ? (
                  <input
                    type="text"
                    value={editGreeting}
                    onChange={(e) => setEditGreeting(e.target.value)}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300"
                    placeholder="Saludo"
                  />
                ) : (
                  <p className="text-foreground-800 text-xs leading-relaxed font-medium">{greeting}</p>
                )}

                {/* Message */}
                {editMode ? (
                  <textarea
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 resize-none"
                    placeholder="Mensaje del email (HTML permitido)"
                  />
                ) : (
                  <div
                    className="text-foreground-600 text-xs leading-relaxed space-y-2"
                    dangerouslySetInnerHTML={{ __html: message }}
                  ></div>
                )}

                {/* CTA Button */}
                {currentTemplate.showCta && (
                  <div className="pt-3">
                    {editMode ? (
                      <input
                        type="text"
                        value={editCtaText}
                        onChange={(e) => setEditCtaText(e.target.value)}
                        className="w-full px-5 py-3 rounded-xl text-white text-xs font-bold outline-none text-center cursor-pointer"
                        style={{ backgroundColor: currentTemplate.ctaColor }}
                        placeholder="Texto del botón"
                      />
                    ) : (
                      <div className="flex justify-center">
                        <div
                          className="inline-block px-8 py-3 rounded-xl text-white text-xs font-bold cursor-pointer"
                          style={{ backgroundColor: currentTemplate.ctaColor }}
                        >
                          {ctaText}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ========== FOOTER ========== */}
              <div className="bg-background-100 border-t border-background-200 px-8 py-6 space-y-5">
                {/* Main links row */}
                <div className="flex items-center justify-center gap-6 text-[10px] text-foreground-500 font-medium">
                  <span className="flex items-center gap-1.5 cursor-pointer hover:text-foreground-700 transition-colors">
                    <i className="ri-global-line text-[9px]"></i>
                    Sitio Web
                  </span>
                  <span className="flex items-center gap-1.5 cursor-pointer hover:text-foreground-700 transition-colors">
                    <i className="ri-customer-service-2-line text-[9px]"></i>
                    Soporte
                  </span>
                  <span className="flex items-center gap-1.5 cursor-pointer hover:text-foreground-700 transition-colors">
                    <i className="ri-download-line text-[9px]"></i>
                    Descargar Apps
                  </span>
                </div>

                {/* Divider */}
                <div className="border-t border-background-200/60"></div>

                {/* Social links */}
                {currentTemplate.socialLinks.length > 0 && (
                  <div className="flex items-center justify-center gap-3">
                    {currentTemplate.socialLinks.map((social) => (
                      <div
                        key={social.platform}
                        className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-foreground-200 transition-colors"
                        title={social.platform}
                      >
                        <i className={`${social.icon} text-foreground-500 text-sm`}></i>
                      </div>
                    ))}
                  </div>
                )}

                {/* App download */}
                {currentTemplate.showAppDownload && (
                  <div className="text-center space-y-2">
                    <p className="text-foreground-400 text-[9px] font-medium">Descargá nuestra app</p>
                    <div className="flex items-center justify-center gap-2">
                      <div className="px-3.5 py-2 rounded-lg bg-foreground-950 text-white text-[9px] font-medium flex items-center gap-1.5 cursor-pointer">
                        <i className="ri-apple-fill text-xs"></i>
                        App Store
                      </div>
                      <div className="px-3.5 py-2 rounded-lg bg-foreground-950 text-white text-[9px] font-medium flex items-center gap-1.5 cursor-pointer">
                        <i className="ri-google-play-fill text-xs"></i>
                        Google Play
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-background-200/60"></div>

                {/* Company info */}
                <div className="text-center space-y-1">
                  <p className="text-foreground-600 text-[10px] font-semibold">{currentTemplate.companyName}</p>
                  <p className="text-foreground-400 text-[9px]">{currentTemplate.companyAddress}</p>
                  <p className="text-foreground-400 text-[9px]">{currentTemplate.companyEmail}</p>
                </div>

                {/* Footer text */}
                <p className="text-center text-foreground-400 text-[9px] leading-relaxed">
                  {currentTemplate.footerText}
                </p>

                {/* Unsubscribe */}
                <p className="text-center text-foreground-300 text-[8px]">
                  Si no querés recibir más correos de este tipo, podés{' '}
                  <span className="underline cursor-pointer text-foreground-400">gestionar tus preferencias</span>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Variables section */}
        {!showVersions && (
          <div className="px-5 pb-5">
            <div className="max-w-[640px] mx-auto bg-background-100 border border-background-200 rounded-xl p-4">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">
                Variables disponibles ({currentTemplate.variables.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {currentTemplate.variables.map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background-200 border border-background-200 text-foreground-600 text-[10px] font-mono"
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal footer */}
        <div className="px-5 py-3 border-t border-background-200 flex items-center justify-between">
          <div className="flex items-center gap-3 text-[10px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-[9px]"></i>
              {currentTemplate.lastEdited}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line text-[9px]"></i>
              {currentTemplate.lastEditedBy}
            </span>
          </div>
          {editMode && (
            <button
              onClick={() => setEditMode(false)}
              className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar edición
            </button>
          )}
        </div>
      </div>
    </div>
  );
}